import { Variants } from 'common';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import React, { createContext, FC, ReactNode, useEffect, useMemo, useState } from 'react';

import { toggleColorMode } from 'infrastructure/redux/slices/theme.slice';
import { useAppDispatch } from 'infrastructure/redux/store/hooks';

import appTheme from './theme';

const DARK_QUERY = '(prefers-color-scheme: dark)';

type Mode = 'light' | 'dark';

type ColorTokens = { main: string; dark: string; light: string; contrastText: string };

const getSystemMode = (): Mode =>
  typeof window !== 'undefined' && window.matchMedia?.(DARK_QUERY).matches ? 'dark' : 'light';

interface IColorModeContext {
  mode: Mode;
}

export const ColorModeContext = createContext<IColorModeContext>({
  mode: 'light',
});

interface IColorModeProvider {
  children: ReactNode;
}

const variant = process.env.VITE_VARIANT as Variants;

// GF: Five Iron primary is mode-dependent lime — dark olive on light backgrounds,
// bright lime with dark text on dark backgrounds. `dark` drives the contained-button
// hover state, so it must be a darker lime (not the legacy pink).
const fiveIronPrimaryByMode: Record<Mode, ColorTokens> = {
  light: { main: '#5C7A00', dark: '#4A6300', light: '#7A9E00', contrastText: '#FFFFFF' },
  dark: { main: '#BFF300', dark: '#A6D400', light: '#CFF94D', contrastText: '#222222' },
};

// GF: Dark token set for the Five Iron brand. Light tokens live in fiveiron/palette.ts;
// these override the surface/text/semantic colors so the chrome follows the OS theme.
const fiveIronDarkPalette = {
  text: { primary: '#FFFFFF', secondary: 'rgba(255, 255, 255, 0.70)' },
  secondary: {
    main: '#14CEC6',
    dark: '#0E908A',
    light: '#43D7D1',
    contrastText: '#08201F',
    states: { selected: '#14CEC629' },
  },
  error: { main: '#F57E7E', dark: '#C13838', light: '#F8A5A5', contrastText: '#FFFFFF' },
  warning: { main: '#FFB547', dark: '#C77700', light: '#FFCB73', contrastText: '#222222' },
  info: { main: '#4FC3F7', dark: '#0288D1', light: '#81D4FA', contrastText: '#222222' },
  success: { main: '#66BB6A', dark: '#388E3C', light: '#81C784', contrastText: '#222222' },
  action: {
    active: 'rgba(255, 255, 255, 0.56)',
    hover: 'rgba(255, 255, 255, 0.08)',
    selected: 'rgba(255, 255, 255, 0.16)',
    focus: 'rgba(255, 255, 255, 0.12)',
    disabled: 'rgba(255, 255, 255, 0.38)',
    disabledBackground: 'rgba(255, 255, 255, 0.12)',
  },
  other: {
    outlineBorder: '#333333',
    divider: 'rgba(255, 255, 255, 0.12)',
    overlay: 'rgba(0, 0, 0, 0.60)',
    highlight: '#F50057',
  },
  background: {
    default: '#121212',
    paper: '#1E1E1E',
    neutral: 'rgba(255, 255, 255, 0.16)',
    subtle: '#1E1E1E',
  },
};

export const ColorModeProvider: FC<IColorModeProvider> = ({ children }) => {
  const dispatch = useAppDispatch();
  const [mode, setMode] = useState<Mode>(getSystemMode);

  // Follow the OS/browser theme, including live changes while the app is open.
  useEffect(() => {
    const mql = window.matchMedia?.(DARK_QUERY);
    if (!mql) return undefined;
    const handler = (event: MediaQueryListEvent) => setMode(event.matches ? 'dark' : 'light');
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    dispatch(toggleColorMode({ isDarkTheme: mode === 'dark' }));
  }, [mode, dispatch]);

  const defaultTheme = useMemo(() => {
    // Only Five Iron follows the system theme; other brands keep their fixed palette.
    if (variant !== Variants.FIVEIRON) return appTheme(variant);
    const paletteOverride = {
      primary: fiveIronPrimaryByMode[mode],
      ...(mode === 'dark' ? fiveIronDarkPalette : {}),
    };
    return appTheme(variant, mode, paletteOverride);
  }, [mode]);

  return (
    <ColorModeContext.Provider value={{ mode }}>
      <ThemeProvider theme={defaultTheme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};
